<template>
    <div class="attention-section">
        <section-header title="Needs Your Attention" icon="fa-flag" :count="items.length ? items.length : null"/>

        <card-skeleton v-if="loading" :lines="3"/>

        <ui-card v-else-if="items.length === 0">
            <empty-state variant="card" icon="fa-circle-check" title="You're all caught up." text="Nothing in your courses or conference records needs action right now."/>
        </ui-card>

        <ui-card v-else class="attention-card">
            <div v-for="it in visible" :key="it.id" :class="['attention-row', 'is-' + it.severity]" @click="open(it)" role="link" tabindex="0" @keydown.enter="open(it)">
                <div class="row-icon"><i :class="['fad', meta(it).icon, 'fa-fw']"></i></div>
                <div class="row-body">
                    <div class="row-title">{{ it.title }}</div>
                    <div class="row-subtitle" v-if="it.subtitle">{{ it.subtitle }}</div>
                    <div class="row-note" v-if="it.note"><i class="fas fa-circle-info mr-2"></i>{{ it.note }}</div>
                </div>
                <!-- The row itself is the button; the verb is a plain link
                     with the kind's icon and an arrow (external links get
                     the out-arrow instead) -->
                <div class="row-action">
                    <i :class="['fas', meta(it).icon, 'mr-2']"></i>{{ meta(it).cta }}<i :class="['fas', meta(it).external || it.href ? 'fa-arrow-up-right-from-square' : 'fa-arrow-right', 'ml-2']"></i>
                </div>
            </div>
            <div class="show-all" v-if="items.length > limit">
                <a href @click.prevent="expanded = !expanded">
                    <span v-if="!expanded">Show all {{ items.length }}</span>
                    <span v-else>Show fewer</span>
                </a>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import SectionHeader from "../framework/SectionHeader";
    import EmptyState from "../framework/EmptyState";
    import CardSkeleton from "../framework/CardSkeleton";
    import UiCard from "../UiCard";
    import {attentionMeta} from "../../constants/attention";

    /**
     * The unified "needs your attention" list. Items arrive sorted from the
     * API (severity, then due date, then recency); the list caps at `limit`
     * rows with a show-all toggle. Rows route into the course/conference
     * page that resolves the item, or open the external (TPO) link.
     */
    export default {
        name: "AttentionList",
        components: {SectionHeader, EmptyState, CardSkeleton, UiCard},
        props: {
            items: {type: Array, default: () => []},
            loading: {type: Boolean, default: false},
            limit: {type: Number, default: 6},
        },
        data() {
            return {expanded: false};
        },
        computed: {
            visible() {
                return this.expanded ? this.items : this.items.slice(0, this.limit);
            }
        },
        methods: {
            meta(it) {
                return attentionMeta(it.kind);
            },
            open(it) {
                if (it.route) {
                    this.$router.push(it.route).catch(() => {});
                } else if (it.href) {
                    window.open(it.href, '_blank', 'noopener');
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    // Rows carry their own padding; the card clips the severity border to
    // its rounded corners so the colour runs edge to edge
    .attention-card {
        overflow: hidden;

        ::v-deep .ui-card-body {
            padding: 0;
        }
    }

    // Rows are the buttons: a plain verb + arrow on the right, no CTA strip
    // (Derek 2026-09-02, after trying the strip)
    .attention-row {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1rem 1.5rem 1rem 1.25rem;
        border-left: 4px solid transparent;
        cursor: pointer;
        transition: background 0.12s ease;

        & + .attention-row {
            border-top: 1px solid #f0f0f0;
        }

        &:hover, &:focus {
            background: #f7f9fc;
            outline: none;

            .row-action {
                color: #28517a;
                text-decoration: underline;
            }
        }

        // Icon and action sit on the title line; the body can grow below them
        .row-icon {
            font-size: 1.25rem;
            width: 1.75rem;
            text-align: center;
            flex-shrink: 0;
            line-height: 1.5;
        }

        .row-body {
            flex: 1;
            min-width: 0;
        }

        .row-action {
            flex-shrink: 0;
            white-space: nowrap;
            font-size: 0.85rem;
            font-weight: 600;
            color: #336699;
            line-height: 1.5;
            padding-top: 0.1rem;
        }

        .row-title {
            font-weight: 600;
            color: #333;
        }

        .row-subtitle {
            font-size: 0.85rem;
            color: #777;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .row-note {
            margin-top: 0.4rem;
            font-size: 0.8rem;
            font-weight: 500;
            color: #9a5d00;
            white-space: normal;

            i {
                color: #e28b05;
            }
        }

        &.is-critical {
            border-left-color: #9b0d0d;

            .row-icon {
                color: #9b0d0d;
            }
        }

        &.is-action {
            border-left-color: #e28b05;

            .row-icon {
                color: #e28b05;
            }
        }

        &.is-info {
            border-left-color: #336699;

            .row-icon {
                color: #336699;
            }
        }

        @media (max-width: 600px) {
            padding: 0.75rem 1rem;
            flex-wrap: wrap;

            .row-action {
                width: 100%;
                text-align: right;
            }
        }
    }

    .show-all {
        padding: 0.6rem 1.5rem 0.75rem;
        font-size: 0.85rem;
        border-top: 1px solid #f0f0f0;
    }
</style>
